import React, { useState, useEffect } from 'react';
import api from '../services/api';
import KnowledgeList from '../components/knowledge/KnowledgeList';
import KnowledgeForm from '../components/knowledge/KnowledgeForm';
import DashboardContainer from '../components/layout/DashboardContainer';
import { useNotification } from '../context/NotificationContext';
import { BookOpen, Search, Upload, Plus } from 'lucide-react';
import './KnowledgePage.css';

function KnowledgePage() {
    const { showSuccess, showError } = useNotification();
    const [items, setItems] = useState([]);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadItems();
    }, [category]);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (category) params.category = category;
            const { data } = await api.get('/knowledge', { params });
            setItems(Array.isArray(data) ? data : data.items || []);
        } catch (err) {
            console.error("Failed to load knowledge base", err);
            setError("Failed to load knowledge base items.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (itemData) => {
        try {
            if (editingItem) {
                await api.put(`/knowledge/${editingItem.id || editingItem._id}`, itemData);
            } else {
                await api.post('/knowledge', itemData);
            }
            setShowForm(false);
            setEditingItem(null);
            loadItems();
        } catch (err) {
            console.error("Failed to save item", err);
            showError(err.response?.data?.detail || "Failed to save item. Please try again.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this knowledge item? It will no longer be available to the AI.')) return;
        try {
            await api.delete(`/knowledge/${id}`);
            loadItems();
        } catch (err) {
            console.error("Failed to delete item", err);
            showError(err.response?.data?.detail || "Failed to delete item.");
        }
    };

    const handleUpload = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            setLoading(true);
            await api.post('/knowledge/upload', formData, {
                params: { category: category || 'general' },
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showSuccess('Document uploaded and grouped into readable knowledge items.');
            loadItems();
        } catch (err) {
            console.error("Failed to upload document", err);
            showError(err.response?.data?.detail || "Failed to upload document.");
            setLoading(false);
        }
    };

    return (
        <DashboardContainer>
            <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
                <div className="page-toolbar">
                    <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h1 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px 0' }}>
                                <BookOpen size={24} /> Knowledge Base
                            </h1>
                            <p style={{ margin: 0, color: '#666' }}>Add business information here for your AI Assistant to use when answering customer questions.</p>
                        </div>
                        <div className="page-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <label className="btn" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '8px 16px', fontWeight: '500', height: 'fit-content' }}>
                                <Upload size={16} /> Upload PDF
                                <input type="file" accept=".pdf,application/pdf" hidden
                                    onChange={e => handleUpload(e.target.files[0])} />
                            </label>
                            <button className="btn primary block" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontWeight: '500', height: 'fit-content' }} onClick={() => { setEditingItem(null); setShowForm(true); }}>
                                <Plus size={16} /> Add Item
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card padded" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                    <select value={category} onChange={e => setCategory(e.target.value)} className="select" style={{ minWidth: '180px' }}>
                        <option value="">All Categories</option>
                        <option value="general">General</option>
                        <option value="menu">Menu Items</option>
                        <option value="hours">Business Hours</option>
                        <option value="faq">FAQ</option>
                        <option value="policy">Policies</option>
                    </select>
                    <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                        <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
                        <input
                            type="text"
                            placeholder="Search knowledge..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="input"
                            style={{ width: '100%', paddingLeft: '32px' }}
                        />
                    </div>
                </div>

                <p style={{ marginTop: '-8px', marginBottom: '16px', color: '#666', fontSize: '0.92rem' }}>
                    PDF upload is supported. Uploaded content is grouped into knowledge entries instead of raw line-by-line text.
                </p>

                {error && <div className="error-message">{error}</div>}

                {/* Items grouped by category */}
                {loading ? (
                    <div className="loading-state">Loading knowledge base...</div>
                ) : (
                    <KnowledgeList
                        items={items}
                        searchQuery={search}
                        onEdit={(item) => { setEditingItem(item); setShowForm(true); }}
                        onDelete={handleDelete}
                    />
                )}

                {/* Add/Edit modal */}
                {showForm && (
                    <KnowledgeForm
                        item={editingItem}
                        onSave={handleSave}
                        onClose={() => { setShowForm(false); setEditingItem(null); }}
                    />
                )}
            </div>
        </DashboardContainer>
    );
}

export default KnowledgePage;
